import type { CollectionQueryBuilder } from '@nuxt/content'
import { LAYERS, getCollectionName, type LayerName } from './constants'

export type Layered<T> = T & { _layer: LayerName }

export interface LayeredContent {
    query<T = Record<string, unknown>>(contentType: string, path: string): Promise<Layered<T> | null>
    /** Get all items from all layers, deduped by slug. */
    queryAll<T = Record<string, unknown>>(contentType: string): Promise<Layered<T>[]>
    /** Get items matching a category folder, ordered by sortOrder. Deduped by slug. */
    queryByCategory<T = Record<string, unknown>>(contentType: string, category: string): Promise<Layered<T>[]>
    queryBySlug<T = Record<string, unknown>>(contentType: string, slug: string): Promise<Layered<T> | null>
    /** Search items by title. Deduped by slug. */
    searchByTitle<T = Record<string, unknown>>(contentType: string, searchTerm: string): Promise<Layered<T>[]>
}

type QueryCollection = (collection: string) => CollectionQueryBuilder<any>

export interface LayeredContentOptions {
    /**
     * Look a single item up layer by layer, stopping at the first hit, instead of on every layer at
     * once. For the browser, where each layer's first query downloads that layer's collection.
     */
    cascade?: boolean
    /**
     * Whether a collection is registered. Given, only collections it rejects count as empty and query
     * errors throw; otherwise every query error counts as empty.
     */
    hasCollection?: (collection: string) => boolean
}

declare module 'h3' {
    interface H3EventContext {
        /** Layered content for SSR. */
        layeredContent: LayeredContent
    }
}

function stripLayerPrefix(path: string): string {
    return path.replace(/^\/?(brand|region|base)/, '')
}

/**
 * Queries content across the brand → region → base layers of the dynamic collections created by
 * `defineContentLayers`, keeping the highest-priority result; a collection a layer lacks counts as
 * empty. A single-item lookup asks the brand layer alone first, where most items live, and region
 * and base together only on a miss, unless `cascade`. Every other lookup asks all layers at once.
 */
export function createLayeredContent(queryCollection: QueryCollection, { cascade = false, hasCollection }: LayeredContentOptions = {}): LayeredContent {
    async function orEmpty<T>(collection: string, run: () => Promise<T>, empty: T): Promise<T> {
        if (hasCollection) return hasCollection(collection) ? run() : empty

        try {
            return await run()
        }
        catch {
            return empty
        }
    }

    const first = (collection: string, path: string) =>
        orEmpty(collection, () => queryCollection(collection).path(path).first(), null)

    const perLayer = <T>(contentType: string, lookup: (collection: string, layer: LayerName) => Promise<T>) =>
        Promise.all(LAYERS.map(layer => lookup(getCollectionName(contentType, layer), layer)))

    async function highestLayer<T>(contentType: string, lookup: (collection: string, layer: LayerName) => Promise<unknown>): Promise<Layered<T> | null> {
        if (cascade) {
            for (const layer of LAYERS) {
                const result = await lookup(getCollectionName(contentType, layer), layer)

                if (result) return { ...(result as T), _layer: layer }
            }

            return null
        }

        const [top, ...rest] = LAYERS
        const topResult = await lookup(getCollectionName(contentType, top), top)

        if (topResult) return { ...(topResult as T), _layer: top }

        const results = await Promise.all(rest.map(layer => lookup(getCollectionName(contentType, layer), layer)))
        const index = results.findIndex(Boolean)

        return index === -1 ? null : { ...(results[index] as T), _layer: rest[index]! }
    }

    /** Deduplicate items across layers — keeps the highest-priority layer per slug. */
    function mergeLayers<T>(results: unknown[][]): Layered<T>[] {
        const seen = new Set<string>()

        return results
            .flatMap((items, index) => items.map(item => ({ ...(item as T), _layer: LAYERS[index]! })))
            .filter((item) => {
                const slug = stripLayerPrefix((item as { stem?: string }).stem ?? '')
                if (seen.has(slug)) return false
                seen.add(slug)
                return true
            })
    }

    return {
        async query<T>(contentType: string, path: string) {
            const normalized = stripLayerPrefix(path.startsWith('/') ? path : `/${path}`)

            return highestLayer<T>(contentType, async (collection, layer) => {
                const result = await first(collection, `/${layer}${normalized}`)

                return result || (normalized === '/index' ? await first(collection, `/${layer}`) : null)
            })
        },

        async queryAll<T>(contentType: string) {
            return mergeLayers<T>(await perLayer(contentType, collection =>
                orEmpty(collection, () => queryCollection(collection).all(), [])))
        },

        async queryByCategory<T>(contentType: string, category: string) {
            return mergeLayers<T>(await perLayer(contentType, collection =>
                orEmpty(collection, () => queryCollection(collection)
                    .where('stem', 'LIKE', `%/${category}/%`)
                    .order('sortOrder', 'ASC')
                    .all(), [])))
        },

        async queryBySlug<T>(contentType: string, slug: string) {
            return highestLayer<T>(contentType, async (collection) => {
                const items = await orEmpty(collection, () => queryCollection(collection).where('stem', 'LIKE', `%/${slug}`).all(), [])

                return items[0] ?? null
            })
        },

        async searchByTitle<T>(contentType: string, searchTerm: string) {
            if (!searchTerm || searchTerm.length < 2) return []

            return mergeLayers<T>(await perLayer(contentType, collection =>
                orEmpty(collection, () => queryCollection(collection).where('title', 'LIKE', `%${searchTerm}%`).all(), [])))
        },
    }
}
